/**
 * @voltxampere/web — Canvas + theme preferences (Sprint 23 F1)
 *
 * AYARLAR > Görünüm sub-tab'ının placeholder toggle'larını gerçek
 * state'e bağlar. Settings store'a (Sprint 23 C1) theme alanı
 * persist edilir.
 *
 * Default'lar Sprint 18 Paket B B7 mockup'tan: night canvas,
 * catenary wires, IEC symbols.
 *
 * Public API:
 *  - canvasStyle, wireCurve, symbolStandard signal'ları
 *  - setCanvasStyle / setWireCurve / setSymbolStandard mutators
 *    (signal update + IDB persist fire-and-forget)
 *  - initCanvasPrefs() — boot path'inde IDB hydrate
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings, type ThemeSettings } from "@voltxampere/persistence";

import { fxAnimationEnabled, voltageGradientEnabled, voltageHeatmapEnabled } from "./fx-state.js";
import { audioAutoplayHinted, audioMasterVolume, audioMuted } from "./audio-state.js";

export type CanvasStyle = ThemeSettings["canvasStyle"];
export type WireCurve = ThemeSettings["wireCurve"];
export type SymbolStandard = ThemeSettings["symbolStandard"];

export const canvasStyle = signal<CanvasStyle>("night");
export const wireCurve = signal<WireCurve>("catenary");
export const symbolStandard = signal<SymbolStandard>("iec");

let initialised = false;

/**
 * Sprint 30 v2 — settings record version. v1 (Sprint 23 F1) wrote the
 * three style fields only; v2 (Sprint 30) adds fxAnimation, voltageHeatmap,
 * voltageGradient. Migration is implicit: undefined v1 fields hydrate to
 * `true` defaults on first load, then persist() writes the full v2 shape
 * back the next time the user touches a toggle.
 */
export async function initCanvasPrefs(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    if (settings?.theme) {
      // v1 fields (Sprint 23 F1)
      canvasStyle.value = settings.theme.canvasStyle;
      wireCurve.value = settings.theme.wireCurve;
      symbolStandard.value = settings.theme.symbolStandard;
      // v2 fields (Sprint 30) — backward compatible: undefined → true
      fxAnimationEnabled.value = settings.theme.fxAnimation ?? true;
      voltageHeatmapEnabled.value = settings.theme.voltageHeatmap ?? true;
      voltageGradientEnabled.value = settings.theme.voltageGradient ?? true;
      // v3 fields (Sprint 31) — backward compatible: undefined → defaults
      audioMuted.value = settings.theme.audioMuted ?? false;
      audioMasterVolume.value = settings.theme.audioMasterVolume ?? 0.5;
      audioAutoplayHinted.value = settings.theme.audioAutoplayHinted ?? false;
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA canvas-prefs] hydrate failed:", err);
    }
  }
}

function persist(): void {
  void mergeSettings({
    theme: {
      canvasStyle: canvasStyle.value,
      wireCurve: wireCurve.value,
      symbolStandard: symbolStandard.value,
      // Sprint 30 v2 fields — write back together so a single record
      // upgrades from v1 to v2 atomically the first time the user
      // touches any toggle.
      fxAnimation: fxAnimationEnabled.value,
      voltageHeatmap: voltageHeatmapEnabled.value,
      voltageGradient: voltageGradientEnabled.value,
      // Sprint 31 v3 fields — same atomic upgrade strategy.
      audioMuted: audioMuted.value,
      audioMasterVolume: audioMasterVolume.value,
      audioAutoplayHinted: audioAutoplayHinted.value,
    },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA canvas-prefs] persist failed:", err);
    }
  });
}

/** Public so fx-state.ts mutators can trigger persist after flipping a signal. */
export function persistThemePrefs(): void {
  persist();
}

export function setCanvasStyle(style: CanvasStyle): void {
  canvasStyle.value = style;
  persist();
}

export function setWireCurve(curve: WireCurve): void {
  wireCurve.value = curve;
  persist();
}

export function setSymbolStandard(std: SymbolStandard): void {
  symbolStandard.value = std;
  persist();
}

export function __resetCanvasPrefsForTests(): void {
  canvasStyle.value = "night";
  wireCurve.value = "catenary";
  symbolStandard.value = "iec";
  initialised = false;
}
